import { doesProductComponentMatch } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { CultureCode, useGeoBasedLocale } from '@shared/hooks/useGeoLocale';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useMemo } from 'react';
import { useCardCategoryContext } from '@apps/card/components/CardCategoryProvider';
import { useIsPublicGallerySsr } from './useIsPublicGallerySsr';
import type { PropsPayload, ValuePropAsset, ValuePropCarouselAsset } from './propValues.types';
import { ENVELOPES_VALUE_PROP_TELEMETRY_ID, VALUE_PROPS_PAYLOAD } from './valueProps.constants';

const cardCategoryMatches = (categoriesList: ReadonlyArray<string>, context: { occasion: string; category: StationeryTemplateCategoryEnum }) => {
  return categoriesList.some(category => {
    const categoryComponents = category.split('/');
    const [targetOccasion, targetCategory] = categoryComponents;
    const { category: currentCategory, occasion: currentOccasion } = context;

    if (!doesProductComponentMatch(targetOccasion, currentOccasion)) {
      return false;
    }

    if (!doesProductComponentMatch(targetCategory, currentCategory)) {
      return false;
    }

    return true;
  });
};

/**
 * A tile without `visibleInCountries` is global; one with a list is shown only to viewers whose
 * geo-resolved country is in it. `culture` is an approximation (URL param > stored selection >
 * event locale > browser locale > US), not a real IP lookup — good enough for deciding whether
 * to advertise a regional perk, not for anything that has to be exact.
 */
const isVisibleInCountry = (visibleInCountries: ReadonlyArray<string> | undefined, country: string) => {
  return !visibleInCountries || visibleInCountries.includes(country);
};

const isEnvelopeProp = (card: ValuePropAsset | ValuePropCarouselAsset) => 'telemetryId' in card && card.telemetryId === ENVELOPES_VALUE_PROP_TELEMETRY_ID;

/** Drops the envelope slide from the carousel, leaving every other tile untouched. */
const withoutEnvelopeSlide = (card: ValuePropAsset | ValuePropCarouselAsset) =>
  'slides' in card ? { ...card, slides: card.slides.filter(slide => slide.telemetryId !== ENVELOPES_VALUE_PROP_TELEMETRY_ID) } : card;

/**
 * The value-prop tiles for the current category and the viewer's country.
 *
 * The standalone-envelopes prop is authored in {@link VALUE_PROPS_PAYLOAD} and removed here while
 * `printEnvelopeProduct` is off. The flag resolves in every category — tracking an exposure in each
 * — because the carousel renders everywhere, so skipping it would strand the slide on a dead flag.
 */
export const useValuePropsPayload = (): PropsPayload => {
  const { currentCategory: category } = useCardCategoryContext();
  const { culture: geoCulture } = useGeoBasedLocale();
  // On a server-rendered gallery (PRNT-5494) the server has no browser locale, so a
  // country-restricted prop is decided from the server's default country until mount, then from
  // the visitor's; deciding it from the browser on the first render would mismatch the server HTML.
  const isPublicGallerySsr = useIsPublicGallerySsr();
  const isMounted = useIsMounted();
  const culture = isPublicGallerySsr && !isMounted ? CultureCode.US : geoCulture;
  const [isEnvelopeProductEnabled] = useIsFeatureEnabled('printEnvelopeProduct');

  return useMemo(() => {
    const isVisible = (card: ValuePropAsset | ValuePropCarouselAsset) =>
      cardCategoryMatches(card.categoryList, { occasion: 'wedding', category }) && isVisibleInCountry(card.visibleInCountries, culture);

    // `isLoading` is deliberately ignored. This route hands the provider no SSR variants, so the
    // flag can resolve after the first render on any cold load and insert the prop mid-array,
    // shifting the rows below it. Gating on `isLoading` would delay value props for every shopper
    // instead. (A `?feature.` override skips the Loading window, so it will not reproduce.)
    const authoredProps = isEnvelopeProductEnabled ? VALUE_PROPS_PAYLOAD.props : VALUE_PROPS_PAYLOAD.props.filter(card => !isEnvelopeProp(card)).map(withoutEnvelopeSlide);

    return {
      ...VALUE_PROPS_PAYLOAD,
      props: authoredProps.filter(isVisible).map(card => ({ ...card, row: 0, column: 0 })),
    };
  }, [category, culture, isEnvelopeProductEnabled]);
};
